import React, { useEffect, useState } from 'react';
import { Autocomplete, CircularProgress, Paper, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FieldHookConfig, useField } from 'formik';
import { matchSorter } from 'match-sorter';
import { setFieldOnSessionStorage } from '../../helpers/helpers';
import { SelectOption } from '../../types';

type SelectFieldProps = FieldHookConfig<string> & {
  label: string;
  id: string;
  autoComplete?: string;
  formName?: string;
  objectId?: string;
  required?: boolean;
  smallPadding?: boolean;
  disableFiltering?: boolean;
  options: SelectOption[];
  isLoading?: boolean;
  freeSolo?: boolean;
  loadingText?: string;
  error?: string;
  clearError?: (errorKey: string) => void;
  helperText?: string;
  onTextChange?(...data: any[]): void;
  onSelectionChange?(...data: any[]): void;
  renderOption?: (props, option: SelectOption) => React.ReactNode;
};

const CustomPaper = (props: any) => <Paper elevation={8} {...props} />;

const FormikSelect = (props: SelectFieldProps) => {
  const [field, meta, helpers] = useField(props);
  const {
    label,
    id,
    formName,
    objectId = '',
    autoComplete = 'on',
    required = false,
    options,
    smallPadding = false,
    isLoading = false,
    disableFiltering = false,
    freeSolo = false,
    loadingText = 'errors.loadingOptions',
    error,
    clearError,
    helperText = '',
    onTextChange = () => {
      /* void */
    },
    onSelectionChange = () => {
      /* void */
    },
    renderOption,
  } = props;
  const { t } = useTranslation();
  const { setValue } = helpers;
  const [selectedOption, setSelectedOption] = useState<
    { value: string; label: string } | null | string
  >(null);
  const [focused, setFocused] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [textValue, setTextValue] = React.useState('');

  const inputChangeField = (newInputValue: string) => {
    setTextValue(newInputValue);
    const newOption = options.find(
      (option) =>
        option.label?.toString()?.toLowerCase() === newInputValue?.toString()?.toLowerCase()
    );
    clearError?.(id);
    if (newOption) {
      setValue(newOption.value);
      if (formName) {
        setFieldOnSessionStorage(formName, objectId, field.name, newOption.value);
      }
      setSelectedOption({ ...newOption });
      onSelectionChange(newOption);
    } else if (disableFiltering && newInputValue) {
      setValue(newInputValue);
      if (formName) {
        setFieldOnSessionStorage(formName, objectId, field.name, newInputValue);
      }
      setSelectedOption(newInputValue);
    } else if (!newInputValue) {
      if (formName) {
        setFieldOnSessionStorage(formName, objectId, field.name, '');
      }
      setValue('');
      setSelectedOption(freeSolo ? '' : null);
    }
  };

  useEffect(() => {
    if ((!options || options.length === 0) && !freeSolo) return;

    if (field.value !== textValue) {
      let newValue = field.value;
      const newOption = options.find(
        (option) =>
          option.label?.toString()?.toLowerCase() === newValue?.toString()?.toLowerCase() ||
          option.value?.toString()?.toLowerCase() === newValue?.toString()?.toLowerCase()
      );
      if (newOption) newValue = newOption.label;
      inputChangeField(newValue);
    }
  }, [field.value]);

  useEffect(() => {
    if (!options || options.length === 0) return;
    let newValue = field.value;
    const newOption = options.find(
      (option) =>
        option.value?.toString()?.toLowerCase() === field.value?.toString()?.toLowerCase() ||
        option.label?.toString()?.toLowerCase() === field.value?.toString()?.toLowerCase()
    );
    if (newOption) newValue = newOption.label;
    if (newValue !== textValue) inputChangeField(newValue);
  }, [options?.length]);

  const renderOptionProp =
    renderOption === undefined
      ? {}
      : {
          renderOption,
        };

  return (
    <Autocomplete
      fullWidth
      freeSolo={freeSolo}
      sx={{
        '& .MuiAutocomplete-popper': {
          minWidth: '100% !important',
        },
      }}
      id={id}
      loading={isLoading}
      loadingText={t(loadingText)}
      noOptionsText={t('errors.noOptions')}
      options={options}
      inputValue={textValue}
      onInputChange={(e, newInputValue) => {
        if (!e) return;
        inputChangeField(newInputValue);
      }}
      isOptionEqualToValue={(option, value) =>
        (typeof option === 'string' ? option : option.value) ===
        (typeof value === 'string' ? value : value.value)
      }
      open={focused && isOpen}
      onFocus={() => {
        setFocused(true);
        setIsOpen(true);
      }}
      onOpen={(e) => {
        setIsOpen(true);
        onTextChange();
        if (field.value) onTextChange(e as any);
      }}
      onClose={() => setIsOpen(false)}
      onBlur={() => {
        onTextChange();
        setFocused(false);
      }}
      filterOptions={(opts, { inputValue }) =>
        disableFiltering || !inputValue
          ? opts
          : matchSorter(opts, inputValue, { keys: ['value', 'label'] })
      }
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      onChange={(_e, value, details) => {
        if (details === 'clear') {
          onTextChange();
          inputChangeField('');
        }
      }}
      includeInputInList
      value={selectedOption}
      PaperComponent={CustomPaper}
      renderInput={(params) => (
        <TextField
          {...params}
          {...field}
          onChange={(e) => {
            onTextChange(e as any);
          }}
          label={label}
          InputProps={{
            ...params.InputProps,
            inputProps: { ...params.inputProps, autoComplete },
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress
                    sx={{ color: 'text.secondary' }}
                    aria-label="Loading Data"
                    aria-busy={isLoading}
                    aria-live="polite"
                    size={20}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          required={required}
          size={smallPadding ? 'small' : 'medium'}
          helperText={
            (Boolean(error) && error) ||
            (meta.touched && meta.error && t(meta.error, { field: label })) ||
            helperText
          }
          error={Boolean(error) || (meta.touched && Boolean(meta.error))}
          sx={{
            marginBottom: { xs: '16px', sm: '24px' },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'grey.23p',
            },
          }}
        />
      )}
      {...renderOptionProp}
    />
  );
};

export default FormikSelect;
